import { api } from "@/lib/api";

export interface VideoMetadata {
  categoryId: string;
  tagOptionIds?: string[];
  primaryLanguage?: string;
  internalNotes?: string | null;
}

export interface VideoVariant {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription?: string;
  videoFileUrl?: string;
  videoFileName?: string;
  thumbnailUrl?: string;
  headerImageUrl?: string;
  durationSeconds?: number;
  coachId?: string;
  transcript?: string;
  videoRatio?: string;
}

export interface CreateVideoPayload {
  video: VideoMetadata;
  variants: VideoVariant[];
}

export interface UpdateVideoPayload {
  video: VideoMetadata;
  variants: VideoVariant[];
}

export interface VideoListItem {
  id: string;
  versionId: string;
  categoryId: string;
  tagOptionIds: string[];
  primaryTitle: string;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    name: string;
    roles: string[];
  };
}

export interface VideoDetail {
  id: string;
  versionId: string;
  categoryId: string;
  tagOptionIds: string[];
  internalNotes?: string;
  status: string;
  version: number;
  rawFileHash?: string;
  variants: VideoVariant[];
  creator?: {
    id: string;
    name: string;
    roles: string[];
  };
}

export interface InitiateUploadPayload {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface InitiateUploadResponse {
  uploadId: string;
  key: string;
  partUrls: { partNumber: number; url: string }[];
  totalParts: number;
  type: "video";
}

export interface CompleteUploadPayload {
  uploadId: string;
  key: string;
  parts: { PartNumber: number; ETag: string }[];
  title: string;
  description?: string;
}

export interface CompleteUploadResponse {
  id: string;
  type: string;
  status: string;
  rawUrl: string;
  key: string;
  message: string;
}

export const videoService = {
  async list(): Promise<VideoListItem[]> {
    return api.get<VideoListItem[]>("/video");
  },

  async get(id: string): Promise<VideoDetail> {
    return api.get<VideoDetail>(`/video/${id}`);
  },

  async getByVersionId(versionId: string): Promise<VideoDetail> {
    return api.get<VideoDetail>(`/video/version/${versionId}`);
  },

  async create(
    payload: CreateVideoPayload,
  ): Promise<{ id: string; versionId: string }> {
    return api.post<{ id: string; versionId: string }>("/video", payload);
  },

  async update(
    versionId: string,
    payload: UpdateVideoPayload,
  ): Promise<{ id: string; versionId: string }> {
    return api.patch<{ id: string; versionId: string }>(
      `/video/version/${versionId}`,
      payload,
    );
  },

  async submit(videoId: string): Promise<{
    success: boolean;
    videoId: string;
    versionId: string;
    status: string;
  }> {
    return api.post<{
      success: boolean;
      videoId: string;
      versionId: string;
      status: string;
    }>(`/video/${videoId}/submit`, {});
  },

  async archive(videoId: string): Promise<{
    success: boolean;
    videoId: string;
    versionId: string;
    status: string;
  }> {
    return api.post<{
      success: boolean;
      videoId: string;
      versionId: string;
      status: string;
    }>(`/video/${videoId}/archive`, {});
  },

  async restore(videoId: string): Promise<{
    success: boolean;
    videoId: string;
    versionId: string;
    status: string;
  }> {
    return api.post<{
      success: boolean;
      videoId: string;
      versionId: string;
      status: string;
    }>(`/video/${videoId}/restore`, {});
  },

  async createNewVersion(
    videoId: string,
  ): Promise<{ id: string; versionId: string; version: number }> {
    return api.post<{ id: string; versionId: string; version: number }>(
      `/video/${videoId}/new-version`,
      {},
    );
  },

  async initiateUpload(
    payload: InitiateUploadPayload,
  ): Promise<InitiateUploadResponse> {
    return api.post<InitiateUploadResponse>("/video/initiate-upload", payload);
  },

  async completeUpload(
    payload: CompleteUploadPayload,
  ): Promise<CompleteUploadResponse> {
    return api.post<CompleteUploadResponse>("/video/complete-upload", payload);
  },
};
